/* eslint-disable max-lines-per-function */
import { Button, Icon, Snackbar, Tooltip } from '@equinor/eds-core-react';
import { add as ADD, play as PLAY } from '@equinor/eds-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ComputeMethod,
  EstimateVariogramCommand,
  postApiV1JobsComputeVariogramEstimations,
} from '../../../../api/generated';
import { queryKeys } from '../../../../hooks/queryKeys';
import { CaseGroup } from '../../../../features/Compute/Components/CaseGroup/CaseGroup';
import { ComputeHeader } from '../../../../features/Compute/Components/ComputeHeader/ComputeHeader';
import * as Styled from '../Compute.styled';
import { useIsOwnerOrAdmin } from '../../../../hooks/useIsOwnerOrAdmin';
import { useFetchCases } from '../../../../hooks/useFetchCases';
import { useFetchComputeSettings } from '../../../../hooks/useFetchComputeSettings';
import { useFetchModel } from '../../../../hooks/useFetchModel';
import {
  getVariogramMethodOptions,
  isVariogramTypeExhausted,
} from '../../../../features/Compute/utils/variogramCaseUtils';

export interface CaseInfoTyoe {
  type: string;
  title: string;
  info: string;
  addText: string;
  runText: string;
}

const variogramCaseInfo: CaseInfoTyoe = {
  type: 'Variogram',
  title: 'Variogram cases',
  info: 'You can add multiple cases for the different areas in your model.',
  addText: 'Add Variogram case',
  runText: 'Run all variograms',
};

export const ComputeVariogram = () => {
  const isOwnerOrAdmin = useIsOwnerOrAdmin();
  const [showAlert, setAlert] = useState<string>();
  const [triggerAddCase, setTriggerAddCase] = useState<ComputeMethod>();
  const [localCaseList, setLocalCaseList] = useState<Array<string>>([]);
  const { modelId } = useParams<{ modelId: string }>();
  const queryClient = useQueryClient();

  const updateLocalCaseList = (type: string, add: boolean) => {
    if (add) {
      setLocalCaseList((currentList) =>
        currentList.includes(type) ? currentList : [...currentList, type],
      );
    } else {
      setLocalCaseList((currentList) =>
        currentList.filter((item) => item !== type),
      );
      setTriggerAddCase(undefined);
    }
  };

  function clearStatus() {
    setAlert(undefined);
  }

  const computeCasesQuery = useFetchCases({ poll: true });
  const computeSettingsQuery = useFetchComputeSettings();
  const analogueModelQuery = useFetchModel();
  const computeCases = useMemo(
    () => computeCasesQuery.data ?? [],
    [computeCasesQuery.data],
  );
  const computeSettings = computeSettingsQuery.data;
  const analogueModel = analogueModelQuery.data;
  const variogramAvailabilityReady =
    computeCasesQuery.isSuccess &&
    computeSettingsQuery.isSuccess &&
    analogueModelQuery.isSuccess &&
    !!computeSettings &&
    !!analogueModel;

  const methodFilter = (name: ComputeMethod) => {
    return computeCases.filter((method) => method.computeMethod === name);
  };
  const indicator = methodFilter(ComputeMethod.INDICATOR);
  const netToGross = methodFilter(ComputeMethod.NET_TO_GROSS);
  const continiousParameter = methodFilter(ComputeMethod.CONTINIOUS_PARAMETER);

  const exhaustedMethods = useMemo(() => {
    const modelAreas = [
      ...(analogueModel?.modelAreas ?? []),
      { modelAreaId: '', modelAreaType: 'Whole model', coordinates: [] },
    ];
    const methods = [
      ComputeMethod.INDICATOR,
      ComputeMethod.NET_TO_GROSS,
      ComputeMethod.CONTINIOUS_PARAMETER,
    ];

    return methods.reduce<Partial<Record<ComputeMethod, boolean>>>(
      (result, method) => {
        const { parameterOptions, modelOptions, additionalParameterOptions } =
          getVariogramMethodOptions(
            computeSettings?.variogramComputeSettings,
            method,
          );
        result[method] = isVariogramTypeExhausted(
          computeCases,
          method,
          modelAreas,
          parameterOptions,
          modelOptions,
          additionalParameterOptions,
        );
        return result;
      },
      {},
    );
  }, [
    analogueModel?.modelAreas,
    computeCases,
    computeSettings?.variogramComputeSettings,
  ]);

  const setAlertMessage = (message: string) => {
    setAlert(message);
  };

  const addCase = (type: ComputeMethod) => {
    if (!variogramAvailabilityReady) return;
    setTriggerAddCase(type);
  };

  const computeVariogram = useMutation({
    mutationFn: (requestBody: EstimateVariogramCommand) =>
      postApiV1JobsComputeVariogramEstimations({ body: requestBody }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modelCases(modelId as string),
      });
    },
  });
  const runComputeVariogram = async (computeCaseId: string) => {
    if (!modelId) return;
    const requestBody: EstimateVariogramCommand = {
      modelId: modelId,
      computeCaseId: computeCaseId,
    };

    const res = await computeVariogram.mutateAsync(requestBody);

    if (res.data?.success) {
      setAlertMessage('Started computing case');
    }
  };

  return (
    <>
      <Styled.Case>
        <ComputeHeader caseInfo={variogramCaseInfo} />

        <Styled.ButtonDiv>
          <Styled.ButtonGroup>
            <Tooltip
              title={
                !variogramAvailabilityReady
                  ? 'Loading available variogram combinations'
                  : exhaustedMethods[ComputeMethod.INDICATOR]
                  ? 'All variogram combinations for this variogram type are already created'
                  : ''
              }
            >
              <Button
                variant="outlined"
                onClick={() => addCase(ComputeMethod.INDICATOR)}
                disabled={
                  !variogramAvailabilityReady ||
                  triggerAddCase?.includes(ComputeMethod.INDICATOR) ||
                  localCaseList?.includes(ComputeMethod.INDICATOR) ||
                  exhaustedMethods[ComputeMethod.INDICATOR] ||
                  !isOwnerOrAdmin
                }
              >
                <Icon data={ADD} size={18}></Icon>
                Indicator
              </Button>
            </Tooltip>
            <Tooltip
              title={
                !variogramAvailabilityReady
                  ? 'Loading available variogram combinations'
                  : exhaustedMethods[ComputeMethod.NET_TO_GROSS]
                  ? 'All variogram combinations for this variogram type are already created'
                  : ''
              }
            >
              <Button
                variant="outlined"
                onClick={() => addCase(ComputeMethod.NET_TO_GROSS)}
                disabled={
                  !variogramAvailabilityReady ||
                  triggerAddCase?.includes(ComputeMethod.NET_TO_GROSS) ||
                  localCaseList?.includes(ComputeMethod.NET_TO_GROSS) ||
                  exhaustedMethods[ComputeMethod.NET_TO_GROSS] ||
                  !isOwnerOrAdmin
                }
              >
                <Icon data={ADD} size={18}></Icon>
                Net-to-gross
              </Button>
            </Tooltip>
            <Tooltip
              title={
                !variogramAvailabilityReady
                  ? 'Loading available variogram combinations'
                  : exhaustedMethods[ComputeMethod.CONTINIOUS_PARAMETER]
                  ? 'All variogram combinations for this variogram type are already created'
                  : ''
              }
            >
              <Button
                variant="outlined"
                onClick={() => addCase(ComputeMethod.CONTINIOUS_PARAMETER)}
                disabled={
                  !variogramAvailabilityReady ||
                  triggerAddCase?.includes(
                    ComputeMethod.CONTINIOUS_PARAMETER,
                  ) ||
                  localCaseList?.includes(ComputeMethod.CONTINIOUS_PARAMETER) ||
                  exhaustedMethods[ComputeMethod.CONTINIOUS_PARAMETER] ||
                  !isOwnerOrAdmin
                }
              >
                <Icon data={ADD} size={18}></Icon>
                Continious parameter
              </Button>
            </Tooltip>
          </Styled.ButtonGroup>
          <Styled.ButtonGroup>
            <Tooltip title={'Functionality not implemented yet.'}>
              <Button
                variant="outlined"
                // eslint-disable-next-line no-console
                onClick={() => runComputeVariogram}
                disabled
              >
                <Icon data={PLAY} size={18}></Icon>
                Run all
              </Button>
            </Tooltip>
          </Styled.ButtonGroup>
        </Styled.ButtonDiv>

        <CaseGroup
          caseList={
            indicator !== undefined && indicator.length > 0 ? indicator : []
          }
          methodName={ComputeMethod.INDICATOR}
          triggerAddCase={triggerAddCase}
          setAlertMessage={setAlertMessage}
          updateLocalCaseList={updateLocalCaseList}
          runCase={runComputeVariogram}
        />
        <CaseGroup
          caseList={
            netToGross !== undefined && netToGross.length > 0 ? netToGross : []
          }
          methodName={ComputeMethod.NET_TO_GROSS}
          triggerAddCase={triggerAddCase}
          setAlertMessage={setAlertMessage}
          updateLocalCaseList={updateLocalCaseList}
          runCase={runComputeVariogram}
        />

        <CaseGroup
          caseList={
            continiousParameter !== undefined && continiousParameter.length > 0
              ? continiousParameter
              : []
          }
          methodName={ComputeMethod.CONTINIOUS_PARAMETER}
          triggerAddCase={triggerAddCase}
          setAlertMessage={setAlertMessage}
          updateLocalCaseList={updateLocalCaseList}
          runCase={runComputeVariogram}
        />
      </Styled.Case>
      <Snackbar
        open={!!showAlert}
        autoHideDuration={3000}
        onClose={clearStatus}
      >
        {showAlert}
      </Snackbar>
    </>
  );
};
